<template>
    <div v-if="student">
        <h3>{{ student.firstName }} {{ student.lastName }}</h3>
        <router-link :to="{ name: 'students' }" v-if="canViewStudentsList()">Return to Students Lookup</router-link>
        <b-container v-if="student" class="main-container">
            <h5>Programs and Courses</h5>
            <b-row>
                <b-col sm="8">
                    <div v-for="(program, index) in groupedPrograms.slice().reverse()" :key="program[0].studentCareerNumber">
                        <div class="widget">
                            <div class="widget-head">
                                <h3 class="heading">
                                  {{ getPrimaryPlan(program).academicPlanDescription }} ({{ getPrimaryPlan(program).academicPlan }}) {{ getPrimaryPlan(program).academicGroup }}, {{ program[0].currentStatus }}, Last Enrolled: {{ displaySemester(program[0].termCode) }}
                                </h3>
                                <b-button size="sm"
                                          class="expand-button"
                                          @click="toggleProgramVisibility(index)">
                                    <font-awesome-icon :icon="showProgram[index] ? 'minus' : 'plus'" />
                                </b-button>
                            </div>
                            <b-collapse v-model="showProgram[index]" :id="'program' + index">
                                <div class="widget-body">
                                    <b-container>
                                      <b-row>
                                        <b-col sm="6">
                                          <label>Academic Program:</label> {{ program[0].academicProgram }} ({{ program[0].academicCareer }})
                                        </b-col>
                                        <b-col sm="6">
                                          <label>Current Status:</label> {{ program[0].currentStatus }}
                                        </b-col>
                                      </b-row>
                                      <b-row>
                                          <b-col sm="6">
                                              <p>
                                                  <label>Term Admitted:</label> {{ displaySemester(program[0].termAdmitted) }}
                                              </p>
                                          </b-col>
                                          <b-col sm="6">
                                              <p>
                                                  <label>Graduation Semester:</label>
                                                  <span v-if="program[0].graduationTerm">{{ displaySemester(program[0].graduationTerm) }}</span>
                                              </p>
                                          </b-col>
                                      </b-row>
                                      <b-row>
                                        <b-col sm="6">
                                          <label>Last Enrolled:</label> {{ displaySemester(program[0].termCode) }}
                                        </b-col>
                                        <b-col sm="6">
                                          <label>Total Cumulative Credits:</label> {{ program[0].totalCumulative }}
                                        </b-col>
                                      </b-row>
                                      <b-row>
                                          <b-col sm="6">
                                            <label>Cumulative GPA:</label> {{ program[0].cumulativeGpa }}
                                          </b-col>
                                          <b-col sm="6">
                                            <label>Current GPA:</label> {{ program[0].currentGpa }}
                                          </b-col>
                                      </b-row>
                                    </b-container>
                                    <hr />
                                    <p class="program-header">Major</p>
                                    <b-container>
                                        <b-row>
                                            <b-col sm="6">
                                                <label>Plan:</label> {{ getPrimaryPlan(program).academicPlanDescription }} ({{ getPrimaryPlan(program).academicPlan }})
                                            </b-col>
                                            <b-col sm="6">
                                                <label v-if="getPrimaryPlan(program).academicGroup">Group:</label>
                                                <span>{{ getPrimaryPlan(program).academicGroup }}</span>
                                            </b-col>
                                        </b-row>
                                    </b-container>
                                    <div v-if="program.length > 1">
                                        <p class="program-header">Minors</p>
                                        <b-container>
                                            <b-row v-for="minor in getMinorPlans(program)" :key="minor.id">
                                                <b-col sm="6">
                                                    <label>Plan:</label>
                                                    <span>{{minor.academicPlanDescription}} ({{ minor.academicPlan }})</span>
                                                </b-col>
                                                <b-col sm="6">
                                                    <label v-if="minor.academicGroup">Group:</label>
                                                    <span>{{ minor.academicGroup }}</span>
                                                </b-col>
                                            </b-row>
                                        </b-container>
                                    </div>
                                    <hr />
                                    <p class="program-header">Courses</p>
                                    <b-table :items="groupedCourses[program[0].studentCareerNumber]"
                                             :fields="courseFields"
                                             striped
                                             hover
                                             small
                                             outlined
                                             stacked="sm"
                                             thead-class="hidden-header">
                                        <template slot="title" slot-scope="data">
                                            {{ data.item.subjectCode }} {{ data.item.catalogNumber }} - {{ data.item.courseTitle }}
                                        </template>
                                        <template slot="semester" slot-scope="data">
                                            {{ displaySemester(data.item.termCode) }}
                                        </template>
                                    </b-table>
                                </div>
                            </b-collapse>
                        </div>
                    </div>
                    <override-widget :asuriteId="student.asuriteId" v-if="canViewOverrides"></override-widget>
                </b-col>
                <b-col sm="4">
                    <div class="widget">
                        <div class="widget-head">
                            <h3 class="heading">Personal Information</h3>
                        </div>
                        <b-container class="widget-body">
                            <p>{{ student.asuriteId }}</p>
                            <p>
                                <a :href="'mailto:' + student.asuemailAddress">{{ student.asuemailAddress }}</a>
                            </p>
                            <p>
                                {{ student.homeAddr1Ld }}<span v-if="hasHomeAddr2Ld"> {{ student.homeAddr2Ld }}</span>,
                                {{ student.homeCityName }} {{ student.homeStateCode }} {{ student.homePostalCode }}
                            </p>
                            <p>{{ student.preferredPhoneNumber }}</p>
                            <b-button v-if="canImpersonate()" @click="impersonate()">Impersonate</b-button>
                        </b-container>
                    </div>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>
<script>
import OverrideWidget from "../overrides/override-widget";
import { mapState } from "vuex";
import * as _ from "lodash"; // eslint-disable-line no-unused-vars

export default {
  data() {
    return {
      // object of boolean values describing whether a student program should be visible
      showProgram: [true],
      test: false,
      courseFields: [
        { key: "title" },
        { key: "semester" },
        { key: "gradeFinal", label: "Grade" },
        { key: "instructor" }
      ]
    };
  },

  computed: {
    ...mapState({
      user: state => state.auth.user
    }),

    canViewOverrides: function() {
      return _.find(this.user.userRoles, function(userRole) {
        return (
          userRole === "Overrides.Admin" ||
          userRole === "Overrides.Advisor" ||
          userRole === "Student"
        );
      });
    },

    primaryPlan: function() {
      return _.find(this.student.studentPlans, function(studentPlan) {
        return studentPlan.primaryPlan === "Y";
      });
    },

    hasHomeAddr2Ld: function() {
      if (
        _.has(this.student, "homeAddr2Ld") &&
        this.student.homeAddr2Ld &&
        this.student.homeAddr2Ld !== "-"
      ) {
        return true;
      }
      return false;
    },

    // Group student plans on StudentCareerNumber
    groupedPrograms: function() {
      var groupedProgramsArray = [];

      // groupBy returns an object
      var groupedProgramsItem = _.groupBy(
        this.student.studentPlans,
        "studentCareerNumber"
      );

      for (var group in groupedProgramsItem) {
        groupedProgramsArray.push(groupedProgramsItem[group]);
      }

      // convert to array and return
      return groupedProgramsArray;
    },

    // Group student courses by program
    groupedCourses: function() {
      var that = this;
      var groupsObject = {};
      _.forEach(this.groupedPrograms, function(program) {
        var primaryPlan = that.getPrimaryPlan(program);
        var coursesGroup = _.filter(that.courses, function(course) {
          return (
            course.academicCareer === primaryPlan.academicCareer &&
            course.academicGroupCode === primaryPlan.academicGroup &&
            course.academicProgram === primaryPlan.academicProgram
          );
        });
        groupsObject[primaryPlan.studentCareerNumber] = coursesGroup;
      });
      return groupsObject;
    }
  },

  props: {
    student: {
      required: true
    },

    courses: {
      type: Array,
      required: true
    }
  },

  components: {
    "override-widget": OverrideWidget
  },

  methods: {
    // only show the routerLink to the Students List if user is an Admin or Advisor
    canViewStudentsList: function() {
      return _.find(this.user.userRoles, function(userRole) {
        return (
          userRole === "Overrides.Admin" || userRole === "Overrides.Advisor"
        );
      });
    },

    toggleProgramVisibility: function(index) {
      // can't directly alter array by its index because vue will not pick up that change
      if (this.showProgram[index]) {
        this.$set(this.showProgram, index, false);
      } else {
        this.$set(this.showProgram, index, true);
      }
    },

    canImpersonate: function() {
      return _.find(this.user.userRoles, function(userRole) {
        return userRole === "Impersonation";
      });
    },

    impersonate: function() {
      window.location.href = "/impersonation/" + this.student.asuriteId;
    },

    displaySemester: function(termCode) {
      // Year starts with 20
      var semesterString = "20";

      // Last digits of year are the middle two digits of termCode
      semesterString += termCode.substring(1, 3);
      semesterString += " ";

      // Season (fall, winter, etc.) is determined by last digit of termCode
      var seasonId = termCode[3];
      if (seasonId === "1") {
        semesterString += "Spring";
      } else if (seasonId === "4") {
        semesterString += "Summer";
      } else if (seasonId === "7") {
        semesterString += "Fall";
      } else if (seasonId === "9") {
        semesterString += "Winter Intersession";
      }
      return semesterString;
    },

    getPrimaryPlan: function(plans) {
      return _.find(plans, function(plan) {
        return plan.primaryPlan === "Y";
      });
    },

    getMinorPlans: function(plans) {
      return _.filter(plans, function(plan) {
        return plan.primaryPlan === "N";
      });
    }
  }
};
</script>
<style scoped>
label {
  font-weight: bold;
}

p {
  margin-bottom: 0.3em;
}

.student-details {
  padding: 15px 5px 10px 5px;
}

.main-container {
  max-width: 100%;
  margin-top: 10px;
}

.widget-body {
  max-width: 100%;
}

.expand-button {
  line-height: 1;
}

.program-header {
  font-style: italic;
}

.hidden-header {
  display: none;
}
</style>
